import React from 'react';
import { X, Clock, History } from 'lucide-react';
import { CellHistoryEntry } from '../types/spreadsheet';

interface CellHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  columnName: string;
  historyData: CellHistoryEntry[];
  currentValue: string;
}

const CellHistoryModal: React.FC<CellHistoryModalProps> = ({
  isOpen,
  onClose,
  columnName,
  historyData,
  currentValue
}) => {
  if (!isOpen) return null;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const hasHistory = historyData && historyData.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                  <History className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold leading-6 text-gray-900">
                    Histórico de Alterações
                  </h3>
                  <p className="text-sm text-gray-500">
                    Campo: <span className="font-medium">{columnName}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {/* Valor atual */}
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-800">Valor Atual</span>
                  <span className="text-xs text-green-600">
                    {new Date().toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="text-sm text-gray-900 bg-white p-3 rounded border">
                  {currentValue || <span className="text-gray-400 italic">Vazio</span>}
                </div>
              </div>

              {/* Histórico de alterações */}
              {hasHistory ? (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Clock size={16} />
                    Alterações Anteriores ({historyData.length})
                  </h4>
                  {historyData
                    .slice()
                    .reverse() // Mostrar mais recente primeiro
                    .map((entry, index) => (
                      <div key={index} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                          <span className="text-xs text-gray-600">
                            {formatTimestamp(entry.timestamp)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-900 bg-white p-3 rounded border">
                          {entry.value || <span className="text-gray-400 italic">Vazio</span>}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-500">
                    Nenhuma alteração anterior registrada para este campo.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 sm:ml-3 sm:w-auto transition-colors duration-200"
              onClick={onClose}
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CellHistoryModal;